// SEQ-199: total, average, percent of total, rank and cumulative columns.
//
// RULES, WRITTEN DOWN BECAUSE THEY ARE CHOICES
// - null is "no data". It is skipped by total and average, never read as 0,
//   and a row with null gets null in every derived column.
// - average is the simple mean of the row values: sum of the values divided by
//   the count of rows that HAVE a value. This is the spreadsheet's rule
//   ("SUM / count of rows"), not a weighted figure. A percentage column
//   averaged this way is the mean of the row percentages, which is what the
//   spreadsheet's top row shows.
// - percent of total is value / total x 100. When the total is 0 it is null,
//   not 0 and not Infinity. A negative total is allowed and gives the sign the
//   arithmetic gives; the caller decides whether that reads sensibly.
// - rank is 1 for the highest value. Equal values share a rank and the next
//   rank is skipped (1, 2, 2, 4), the usual competition ranking.
// - cumulative columns follow the order of the rows passed in. The caller
//   sorts first (the grid sorts by Total descending), so the transform does
//   not hide a sort of its own.

import { measureOf, type ColumnResult, type TransformRow } from "./types.ts";

/** Sum of the measure over rows that have a value. Null when no row has one. */
export function total(rows: readonly TransformRow[], key: string): number | null {
  let sum = 0;
  let seen = false;
  for (const row of rows) {
    const value = measureOf(row, key);
    if (value === null) continue;
    sum += value;
    seen = true;
  }
  return seen ? sum : null;
}

/** Simple mean over rows that have a value. Null when no row has one. */
export function average(rows: readonly TransformRow[], key: string): number | null {
  let sum = 0;
  let count = 0;
  for (const row of rows) {
    const value = measureOf(row, key);
    if (value === null) continue;
    sum += value;
    count += 1;
  }
  return count === 0 ? null : sum / count;
}

/** value / total x 100 per row. Null when the row has no value or the total is 0 or unknown. */
export function percentOfTotal(rows: readonly TransformRow[], key: string): ColumnResult<number | null> {
  const grand = total(rows, key);
  const out: ColumnResult<number | null> = new Map();
  for (const row of rows) {
    const value = measureOf(row, key);
    out.set(row.id, value === null || grand === null || grand === 0 ? null : (value / grand) * 100);
  }
  return out;
}

/** Competition rank, 1 = highest. Rows with no value get null and do not take a rank. */
export function rank(rows: readonly TransformRow[], key: string): ColumnResult<number | null> {
  const values = rows.map((row) => measureOf(row, key)).filter((v): v is number => v !== null);
  const out: ColumnResult<number | null> = new Map();
  for (const row of rows) {
    const value = measureOf(row, key);
    if (value === null) {
      out.set(row.id, null);
      continue;
    }
    out.set(row.id, 1 + values.filter((other) => other > value).length);
  }
  return out;
}

/** Running total of the measure down the rows, in the order given. A row with no value shows null and carries the running total on. */
export function cumulativeValue(rows: readonly TransformRow[], key: string): ColumnResult<number | null> {
  const out: ColumnResult<number | null> = new Map();
  let running = 0;
  for (const row of rows) {
    const value = measureOf(row, key);
    if (value === null) {
      out.set(row.id, null);
      continue;
    }
    running += value;
    out.set(row.id, running);
  }
  return out;
}

/** Running percent of the total down the rows, in the order given. Null when the total is 0 or unknown. */
export function cumulativePercent(rows: readonly TransformRow[], key: string): ColumnResult<number | null> {
  const grand = total(rows, key);
  const running = cumulativeValue(rows, key);
  const out: ColumnResult<number | null> = new Map();
  for (const row of rows) {
    const value = running.get(row.id) ?? null;
    out.set(row.id, value === null || grand === null || grand === 0 ? null : (value / grand) * 100);
  }
  return out;
}
